/**
 * Zercode API Standard v1 — response envelope & error contract.
 *
 * These are PROTOCOL-level shapes (identical across every service), not
 * endpoint DTOs. Endpoint request/response types are generated from OpenAPI
 * into `src/generated/` and are never hand-written [STD-FE].
 */

/** meta block present on every 2xx response [STD-API-2] */
export interface ResponseMeta {
  correlationId: string;
  timestamp: string;
  version: string;
  pagination?: Pagination;
}

/** pagination block added by list endpoints [STD-API-2] / [STD-API-8] */
export interface Pagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/** standard success envelope: { success, data, meta } [STD-API-2] */
export interface Envelope<TData> {
  success: boolean;
  data: TData;
  meta: ResponseMeta;
}

/**
 * One field-level validation failure, exactly as the wire carries it.
 *
 * ⚠ THE FIELD NAME IS THE SERVER'S PROPERTY PATH — `InfoTitle`, `Question.Options[1].Value` — and it
 * matches no label on any screen. Show the `message`; use the `field` only to route the message to an
 * input (see `fieldErrors`), never as text.
 */
export interface ProblemFieldError {
  field: string;
  message: string;
}

/** RFC 7807 ProblemDetails — identical shape across every service [STD-API-3] */
export interface ProblemDetails {
  /** `https://docs.zercode.com/errors/…` URI */
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  correlationId?: string;
  /**
   * Field-level validation failures.
   *
   * ⚠ TWO SHAPES REACH THIS, and the one this used to declare is the RARER of them. Every Zercode
   * service goes through `ProblemDetailsMapper`, which emits an ARRAY of `{field, message}` as a
   * `[JsonExtensionData]` member — so `Object.entries(problem.errors)` yields `[['0', {…}]]`, i.e. a
   * field named "0" and a message that is an object. Four pages were written against the map shape
   * and therefore surfaced NOTHING for a 400. ASP.NET's own model-binding failures do produce the
   * map. Read this through `ApiProblemError.fieldErrors` / `problemFieldErrors`, which normalise both.
   */
  errors?: ProblemFieldError[] | Record<string, string[]>;
  /** license guard extension [STD-API-3] */
  requiredModule?: string;
  /** quota guard extensions [STD-API-3] */
  quota?: number;
  limit?: number;
  resetAt?: string;
  /** domain errors may extend ProblemDetails further */
  [extension: string]: unknown;
}

/** Error thrown by the client whenever the backend answers with a ProblemDetails body. */
export class ApiProblemError extends Error {
  readonly problem: ProblemDetails;
  readonly correlationId?: string;

  constructor(problem: ProblemDetails) {
    super(problem.title ?? 'API error');
    this.name = 'ApiProblemError';
    this.problem = problem;
    this.correlationId = problem.correlationId;
  }

  get status(): number {
    return this.problem.status;
  }

  /** field-level validation errors, keyed the way a form binds its inputs [STD-API-3] */
  get fieldErrors(): Record<string, string[]> {
    return problemFieldErrors(this.problem);
  }

  /**
   * Every field message, in the order the server listed them — what to SHOW when a page has nowhere
   * to put them field by field. Empty for a non-validation problem, whose story is in `detail`.
   */
  get validationMessages(): string[] {
    return problemFieldMessages(this.problem);
  }
}

/** `InfoTitle` -> `infoTitle`, `Question.Options[1].Value` -> `question.options[1].value`. */
function toFieldKey(field: string): string {
  return field
    .split('.')
    .map((segment) => (segment ? segment.charAt(0).toLowerCase() + segment.slice(1) : segment))
    .join('.');
}

/**
 * Normalise either wire shape into the map a form binds against — `{ firstName: ['…'] }`.
 *
 * The key keeps the FULL property path, lower-camelled per segment, because that is what the server
 * named: a top-level `FirstName` becomes `firstName` (which is what every allow-list in this repo
 * tests), and a nested `Question.Options[1].Value` stays distinguishable rather than collapsing onto
 * a sibling. Several messages on one field accumulate rather than overwrite.
 */
export function problemFieldErrors(problem: ProblemDetails | null | undefined): Record<string, string[]> {
  const raw = problem?.errors;
  if (!raw) return {};

  const out: Record<string, string[]> = {};
  const push = (field: string, message: string) => {
    if (!message) return;
    const key = toFieldKey(field);
    (out[key] ??= []).push(message);
  };

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (entry && typeof entry === 'object' && typeof entry.field === 'string' && typeof entry.message === 'string') {
        push(entry.field, entry.message);
      }
    }
    return out;
  }

  for (const [field, messages] of Object.entries(raw)) {
    // The map shape is ASP.NET's; a stray non-array value must not become "[object Object]" on screen.
    if (Array.isArray(messages)) {
      for (const message of messages) {
        if (typeof message === 'string') push(field, message);
      }
    } else if (typeof messages === 'string') {
      push(field, messages);
    }
  }

  return out;
}

/**
 * The field messages as a flat list, order preserved and duplicates dropped.
 *
 * ⚠ THIS IS WHAT AN ALERT SHOULD RENDER, not `detail`. A validation 400's `detail` is the fixed
 * string "One or more validation errors occurred." — it names neither the field nor the rule, so an
 * alert built on `detail` alone tells the user nothing at all.
 */
export function problemFieldMessages(problem: ProblemDetails | null | undefined): string[] {
  const seen = new Set<string>();
  for (const messages of Object.values(problemFieldErrors(problem))) {
    for (const message of messages) seen.add(message);
  }
  return [...seen];
}

export function isApiProblemError(err: unknown): err is ApiProblemError {
  return err instanceof ApiProblemError;
}
